/* cpap-mask-size-scanner.js — <cpap-mask-size-scanner> custom element.
   Camera + MediaPipe FaceLandmarker guided-capture wizard, ported from the
   working prototype at breezo/utilities/face-mask-sizer-poc/ (app.js).
   Plain HTMLElement (no Shadow DOM), same pattern as cpap-mask-finder.js.
   Config (all families + thresholds) comes from a sibling
   <script type="application/json"> block, same pattern as cpap-mask-finder.

   Flow: pick your mask FIRST (a card grid, split into "camera-ready" vs "no
   camera data yet" — mirrors sections/cpap-mask-sizing-hub.liquid's own
   family grid so this reads as the same site, not a bolted-on tool) → only
   THEN does the camera ever start, and only for a family that's actually
   eligible. Picking an ineligible family just links to its real sizing page
   (/pages/mask-sizing/{handle}) — never a dead end.

   Everything runs client-side; nothing leaves the device (same as the PoC). */

import { FaceLandmarker, FilesetResolver } from 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/vision_bundle.mjs';
import {
  computeAll,
  isFrontal,
  Smoother,
  poseAngles,
  matchesPose,
  POSE_THRESHOLDS,
  frontalityScore,
  symmetryScore,
  keyPointsPx,
  meanDisplacement,
  steadinessScore,
  qualityScore,
  BestSamples,
  isCentered,
  relativePose,
} from './cpap-mask-size-measurements.js?v=2';
import { recommendSize, CONFIDENCE } from './cpap-mask-size-mapping.js?v=2';
import { getMeasurements, saveMeasurements } from './cpap-mask-measurements-store.js?v=1';
import { CartAddEvent } from '@theme/events';

const VISION_WASM = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm';
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

const MEASUREMENT_META = [
  { key: 'ipd', label: 'Interpupillary (IPD)', color: '#4ade80' },
  { key: 'faceWidth', label: 'Face width (ear-to-ear)', color: '#60a5fa' },
  { key: 'noseWidth', label: 'Nose width (alare)', color: '#f472b6' },
  { key: 'noseHeight', label: 'Nose height', color: '#fbbf24' },
  { key: 'mouthWidth', label: 'Mouth width', color: '#a78bfa' },
  { key: 'faceHeight', label: 'Face height (nasion→chin)', color: '#f87171' },
  { key: 'noseBridgeToCrease', label: 'Nose bridge→crease (mask sizing)', color: '#2dd4bf' },
];
const MEASUREMENT_KEYS = MEASUREMENT_META.map((m) => m.key);

const GUIDE_STEPS = [
  { id: 'center', dir: 'center', title: 'Look straight at the camera', sub: 'Center your face' },
  { id: 'up', dir: 'up', title: 'Tip your head up', sub: 'Chin up — look up' },
  { id: 'down', dir: 'down', title: 'Tip your head down', sub: 'Chin down — look down' },
  { id: 'left', dir: 'left', title: 'Turn your head left', sub: 'Look to your left' },
  { id: 'right', dir: 'right', title: 'Turn your head right', sub: 'Look to your right' },
];

const HOLD_MS = 1000;
const STEADY_ADVANCE = 0.5;
const STEADY_SENSITIVITY = 0.2;
const TOP_N = 15;
const RING_C = 2 * Math.PI * 52;

// Steps a customer moves through, in order. Family selection always happens
// before the camera is ever requested. There's no separate "use these
// measurements?" step — if we already have measurements for the customer,
// we resolve straight to RESULTS with the actual size, not a meta-question
// about whether to reuse the data (that screen shipped once and was
// confusing — see the fix note by _showResultsFromStored).
const STEP = { PICKER: 'picker', CAPTURE: 'capture', RESULTS: 'results' };

// Same SVG paths as snippets/cpap-sizing-icon.liquid, kept in sync by hand —
// there's no way to render a Liquid snippet from JS-built markup.
const METHOD_ICON_SVG = {
  template: '<svg viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="1.5"/><path d="M8 8h8M8 12h8M8 16h5"/></svg>',
  fitpack: '<svg viewBox="0 0 24 24"><path d="M3 8l9-5 9 5-9 5z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg>',
  selffit: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8"/><path d="M8 12c1.5 2 2.7 3 4 3s2.5-1 4-3"/><circle class="fill" cx="9.5" cy="10" r=".5"/><circle class="fill" cx="14.5" cy="10" r=".5"/></svg>',
  gauge: '<svg viewBox="0 0 24 24"><path d="M3 13a9 9 0 0118 0"/><path d="M12 13l4-3"/><circle class="fill" cx="12" cy="13" r="1.2"/></svg>',
};
const CHEV_RIGHT_SVG = '<svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6"/></svg>';

const TEMPLATE = `
  <div class="cpapms-picker">
    <div class="mfs-search">
      <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M20 20l-4-4"/></svg>
      <input type="search" class="cpapms-picker-search" placeholder="Search mask families…" aria-label="Search mask families">
    </div>

    <div class="cpapms-picker-section" data-picker-section="eligible">
      <h2 class="cpapms-picker-h2">Measure with your camera</h2>
      <p class="cpapms-picker-sub">These masks have a webcam-based size estimate — pick yours to start.</p>
      <div class="mfs-fam-grid cpapms-picker-grid" data-picker-eligible></div>
    </div>

    <p class="cpapms-picker-empty hidden">No masks match your search.</p>

    <p class="cpapms-picker-fallback">Don't see your mask? Not every mask has a camera estimate yet —
      see the <a href="/pages/mask-sizing">full mask sizing guide</a> for every mask, including printable templates.</p>
  </div>

  <div class="cpapms-selected-bar hidden">
    <span class="cpapms-selected-label">Sizing</span>
    <span class="cpapms-selected-name"></span>
    <button type="button" class="cpapms-btn cpapms-btn--sm cpapms-change-mask">Change mask</button>
  </div>

  <div class="cpapms-stage-wrap hidden">
    <div class="cpapms-stage">
      <video class="cpapms-video" playsinline autoplay muted></video>
      <canvas class="cpapms-overlay"></canvas>
      <div class="cpapms-hint hidden">Position your face in frame</div>
      <div class="cpapms-guide hidden" aria-live="polite">
        <div class="cpapms-guide-top">
          <div class="cpapms-guide-step">Step 1 of 5</div>
          <div class="cpapms-guide-title">Look straight at the camera</div>
          <div class="cpapms-guide-sub">Center your face</div>
        </div>
        <div class="cpapms-guide-arrow" data-dir="center" aria-hidden="true"></div>
        <div class="cpapms-guide-ring" aria-hidden="true">
          <svg viewBox="0 0 120 120">
            <circle class="cpapms-ring-bg" cx="60" cy="60" r="52"></circle>
            <circle class="cpapms-ring-fg" cx="60" cy="60" r="52"></circle>
          </svg>
          <div class="cpapms-ring-pct">0%</div>
        </div>
        <div class="cpapms-guide-dots"></div>
      </div>
    </div>
    <div class="cpapms-controls">
      <button type="button" class="cpapms-btn cpapms-btn-primary cpapms-start">Measure my face</button>
      <button type="button" class="cpapms-btn cpapms-stop" disabled>Stop</button>
      <button type="button" class="cpapms-btn cpapms-switch" disabled>Switch camera</button>
    </div>
    <p class="cpapms-status" data-kind="info">Loading…</p>
  </div>
  <aside class="cpapms-panel-wrap hidden">
    <section class="cpapms-guided-status hidden">
      <h2>Capture quality</h2>
      <div class="cpapms-quality-meters">
        <div class="cpapms-qm-row"><span>Frontality</span><span class="cpapms-qm-bar"><span class="cpapms-qm-fill cpapms-qm-frontality"></span></span></div>
        <div class="cpapms-qm-row"><span>Symmetry</span><span class="cpapms-qm-bar"><span class="cpapms-qm-fill cpapms-qm-symmetry"></span></span></div>
        <div class="cpapms-qm-row"><span>Steadiness</span><span class="cpapms-qm-bar"><span class="cpapms-qm-fill cpapms-qm-steadiness"></span></span></div>
      </div>
      <div class="cpapms-pose-readout">yaw: —  pitch: —</div>
      <div class="cpapms-sample-counts"></div>
    </section>
    <section class="cpapms-results hidden">
      <h2>Your size recommendation</h2>
      <p class="cpapms-results-date hidden"></p>

      <div class="cpapms-recommend">
        <div class="cpapms-recommend-result"></div>
      </div>

      <div class="cpapms-cart-action"></div>
      <p class="cpapms-cart-status hidden" aria-live="polite"></p>

      <p class="cpapms-results-disclaimer">Estimate only, for convenience — always confirm with the official method.
        <button type="button" class="mfs-btn mfs-btn--link" data-chat style="font:inherit">Chat with us</button> if unsure.</p>

      <div class="cpapms-results-actions">
        <button type="button" class="cpapms-btn cpapms-redo">Measure again</button>
        <button type="button" class="mfs-btn mfs-btn--link cpapms-change-mask-2">Choose a different mask</button>
      </div>

      <div class="cpapms-debug-measurements hidden">
        <h3>Raw measurements (debug — append <code>?cpapms_debug=1</code> to the URL to see this)</h3>
        <div class="cpapms-results-list"></div>
        <p class="cpapms-scale-note">IPD should be ~58–68&nbsp;mm for most adults — a quick sanity check.</p>
      </div>
    </section>
  </aside>
`;

class CpapMaskSizeScanner extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.innerHTML = TEMPLATE;
    this.config = this._readConfig();
    // Embedded on a single family's own sizing page (sections/cpap-sizing-family.liquid)
    // — the family is already known from the page itself, so skip the picker
    // entirely and go straight to that one family. "Change mask" then means
    // leaving this page for the full tool, not an inline picker.
    this.singleFamily = this.hasAttribute('data-single-family');

    this.debug = new URLSearchParams(window.location.search).has('cpapms_debug');

    this.els = {
      picker: this.querySelector('.cpapms-picker'),
      pickerSearch: this.querySelector('.cpapms-picker-search'),
      pickerEligibleSection: this.querySelector('[data-picker-section="eligible"]'),
      pickerEligibleGrid: this.querySelector('[data-picker-eligible]'),
      pickerEmpty: this.querySelector('.cpapms-picker-empty'),
      selectedBar: this.querySelector('.cpapms-selected-bar'),
      selectedName: this.querySelector('.cpapms-selected-name'),
      changeMaskBtn: this.querySelector('.cpapms-change-mask'),
      changeMaskBtn2: this.querySelector('.cpapms-change-mask-2'),
      stageWrap: this.querySelector('.cpapms-stage-wrap'),
      video: this.querySelector('.cpapms-video'),
      canvas: this.querySelector('.cpapms-overlay'),
      startBtn: this.querySelector('.cpapms-start'),
      stopBtn: this.querySelector('.cpapms-stop'),
      switchBtn: this.querySelector('.cpapms-switch'),
      status: this.querySelector('.cpapms-status'),
      hint: this.querySelector('.cpapms-hint'),
      guide: this.querySelector('.cpapms-guide'),
      guideStep: this.querySelector('.cpapms-guide-step'),
      guideTitle: this.querySelector('.cpapms-guide-title'),
      guideSub: this.querySelector('.cpapms-guide-sub'),
      guideArrow: this.querySelector('.cpapms-guide-arrow'),
      guideRing: this.querySelector('.cpapms-guide-ring'),
      ringFg: this.querySelector('.cpapms-ring-fg'),
      ringPct: this.querySelector('.cpapms-ring-pct'),
      guideDots: this.querySelector('.cpapms-guide-dots'),
      panelWrap: this.querySelector('.cpapms-panel-wrap'),
      guidedStatus: this.querySelector('.cpapms-guided-status'),
      qmFrontality: this.querySelector('.cpapms-qm-frontality'),
      qmSymmetry: this.querySelector('.cpapms-qm-symmetry'),
      qmSteadiness: this.querySelector('.cpapms-qm-steadiness'),
      poseReadout: this.querySelector('.cpapms-pose-readout'),
      sampleCounts: this.querySelector('.cpapms-sample-counts'),
      results: this.querySelector('.cpapms-results'),
      resultsDate: this.querySelector('.cpapms-results-date'),
      resultsList: this.querySelector('.cpapms-results-list'),
      debugMeasurements: this.querySelector('.cpapms-debug-measurements'),
      redoBtn: this.querySelector('.cpapms-redo'),
      recommendResult: this.querySelector('.cpapms-recommend-result'),
      cartAction: this.querySelector('.cpapms-cart-action'),
      cartStatus: this.querySelector('.cpapms-cart-status'),
    };
    this.ctx = this.els.canvas.getContext('2d');

    this.state = {
      step: STEP.PICKER,
      selectedFamilyIndex: null,
      faceLandmarker: null,
      stream: null,
      running: false,
      rafId: null,
      facingMode: 'user',
      lastVideoTime: -1,
      guided: {
        stepIndex: 0,
        holdMs: 0,
        lastTs: null,
        prevKeyPts: null,
        locking: false,
        finished: false,
        results: null,
        best: new BestSamples([...MEASUREMENT_KEYS, 'scale'], { topN: TOP_N }),
        // Calibration: raw pose reading captured when "Center" locks, so
        // Up/Down/Left/Right are matched relative to THIS camera's neutral
        // rather than a universal constant. null until Center locks.
        baseline: null,
        lastPose: null,
      },
    };

    this.els.debugMeasurements.classList.toggle('hidden', !this.debug);

    this._buildDots();
    if (!this.singleFamily) this._renderPicker();
    this._renderGuideStep();
    this._resetGuidedStatus();

    this.els.startBtn.addEventListener('click', () => this._start());
    this.els.stopBtn.addEventListener('click', () => this._stop());
    this.els.switchBtn.addEventListener('click', () => this._switchCamera());
    this.els.redoBtn.addEventListener('click', async () => {
      // The camera is off once results are showing (see _finishGuided) — restart
      // it here rather than just resetting state, so "Measure again" alone is the
      // one path back into a capture, no separate "Start" click needed.
      this.els.redoBtn.disabled = true;
      await this._start();
      this.els.redoBtn.disabled = false;
    });
    this.els.changeMaskBtn.addEventListener('click', () => this._changeMask());
    this.els.changeMaskBtn2.addEventListener('click', () => this._changeMask());
    this.els.pickerSearch.addEventListener('input', () => this._filterPicker(this.els.pickerSearch.value.trim().toLowerCase()));
    // Delegated — .cpapms-cart-action's contents are rebuilt on every
    // _updateRecommendation() call, so a direct listener wouldn't survive.
    this.els.cartAction.addEventListener('click', (e) => {
      const btn = e.target.closest('.cpapms-add-to-cart');
      if (btn) this._addToCart(btn);
    });

    if (this.singleFamily) {
      this.els.picker.classList.add('hidden');
      if ((this.config.families || []).length > 0) this._selectFamily(0);
    } else {
      this._setStep(STEP.PICKER);
    }
  }

  disconnectedCallback() {
    this._stop();
  }

  _readConfig() {
    const targetId = this.dataset.target;
    const script = targetId ? document.getElementById(targetId) : null;
    if (!script) return { families: [] };
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      console.error('cpap-mask-size-scanner: bad config JSON', err);
      return { families: [] };
    }
  }

  _esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ---------------- step control ---------------- */

  _setStep(step) {
    this.state.step = step;
    this.els.picker.classList.toggle('hidden', step !== STEP.PICKER);
    this.els.selectedBar.classList.toggle('hidden', step === STEP.PICKER);

    const capturing = step === STEP.CAPTURE;
    const showingResults = step === STEP.RESULTS;
    this.els.stageWrap.classList.toggle('hidden', !capturing);
    this.els.guide.classList.toggle('hidden', !(capturing && this.state.running));
    this.els.guidedStatus.classList.toggle('hidden', !capturing);
    this.els.results.classList.toggle('hidden', !showingResults);
    this.els.panelWrap.classList.toggle('hidden', !(capturing || showingResults));
    // During capture, panelWrap sits beside the camera stage (narrower,
    // fixed max-width). Once results show, it's the only thing left on
    // screen — stretch it to match the width of the picker/selected-bar
    // above instead of staying pinned to that narrower camera-sidebar width.
    this.els.panelWrap.classList.toggle('cpapms-panel-wrap--full', showingResults);
  }

  /* ---------------- family picker (step 1) ---------------- */

  _renderPicker() {
    // Config only ever contains eligible families now (see
    // sections/cpap-mask-size-finder.liquid) — this tool doesn't try to be a
    // mini-catalog for masks it can't measure; the "full guide" link below
    // the grid sends those customers to /pages/mask-sizing instead.
    const families = this.config.families || [];
    const indexed = families.map((f, i) => ({ f, i })).sort((a, b) => a.f.name.localeCompare(b.f.name));

    this.els.pickerEligibleGrid.innerHTML = indexed.map(({ f, i }) => this._eligibleCardHtml(f, i)).join('');
    this.els.pickerEligibleGrid.querySelectorAll('.cpapms-picker-card').forEach((btn) => {
      btn.addEventListener('click', () => this._selectFamily(Number(btn.dataset.idx)));
    });
  }

  _cardMediaHtml(f) {
    const img = f.cardImage
      ? `<img src="${this._esc(f.cardImage)}${f.cardImage.includes('?') ? '&' : '?'}width=420" alt="${this._esc(f.name)}" loading="lazy">`
      : `<span class="lbl">${this._esc((f.name || '').toLowerCase())}</span>`;
    const icon = METHOD_ICON_SVG[f.methodIcon] || '';
    return `
      <div class="ph-wrap">
        <div class="mfs-ph" data-tone="sky" style="height:120px">${img}</div>
        <div class="badge-tl"><span class="mfs-mbadge mfs-mbadge--sm" data-method="${this._esc(f.method)}">${icon}${this._esc(f.methodBadge)}</span></div>
      </div>`;
  }

  _eligibleCardHtml(f, idx) {
    const search = this._esc(`${f.brand || ''} ${f.name || ''}`.toLowerCase());
    return `
      <button type="button" class="mfs-card mfs-card--hover mfs-fam cpapms-picker-card" data-idx="${idx}" data-search="${search}">
        ${this._cardMediaHtml(f)}
        <div class="bd">
          <div class="br">${this._esc(f.brand)}</div>
          <div class="nm">${this._esc(f.name)}</div>
          <div class="mt">${this._esc(f.type)} · ${this._esc(f.sizes)}</div>
          <div class="vw">Measure with camera ${CHEV_RIGHT_SVG}</div>
        </div>
      </button>`;
  }

  _filterPicker(query) {
    const terms = query.split(/\s+/).filter(Boolean);
    let anyVisible = false;
    this.els.pickerEligibleGrid.querySelectorAll('.cpapms-picker-card').forEach((card) => {
      const haystack = card.dataset.search || '';
      const match = terms.every((t) => haystack.includes(t));
      card.classList.toggle('hidden', !match);
      if (match) anyVisible = true;
    });
    this.els.pickerEmpty.classList.toggle('hidden', anyVisible);
  }

  _selectFamily(index) {
    const family = (this.config.families || [])[index];
    if (!family || family.axis === 'unsupported') return;
    this.state.selectedFamilyIndex = index;
    this.els.selectedName.textContent = family.name;

    const stored = getMeasurements();
    if (stored) {
      // Go straight to the actual size, don't ask "use these?" first — a
      // screen that only offers "use these measurements / measure again"
      // without ever showing the size makes the customer click through
      // blind to find out what it even says. Show the answer; "Measure
      // again" on the results screen is right there if they want fresh data.
      this._showResultsFromStored(stored);
      return;
    }

    if (!this._checkEnvironment()) return;
    this._resetGuidedSession();
    this._setStep(STEP.CAPTURE);
    this._setStatus('Ready. Tap "Measure my face" and allow camera access.', 'info');
  }

  _showResultsFromStored(stored) {
    const g = this.state.guided;
    g.results = stored.results;
    g.finished = true;
    this._renderResults(g.results);
    const date = new Date(stored.measuredAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    this.els.resultsDate.textContent = `Based on your measurements from ${date}.`;
    this.els.resultsDate.classList.remove('hidden');
    this._setStep(STEP.RESULTS);
  }

  _changeMask() {
    if (this.singleFamily) {
      window.location.href = '/pages/mask-size-measurer';
      return;
    }
    this._stop();
    this.state.selectedFamilyIndex = null;
    this.state.guided.finished = false;
    this.state.guided.results = null;
    this._setStep(STEP.PICKER);
  }

  /* ---------------- shared UI helpers ---------------- */

  _setStatus(msg, kind = 'info') {
    this.els.status.textContent = msg;
    this.els.status.dataset.kind = kind;
  }

  _setHint(msg) {
    if (msg) {
      this.els.hint.textContent = msg;
      this.els.hint.classList.remove('hidden');
    } else {
      this.els.hint.classList.add('hidden');
    }
  }

  _matrixOf(results) {
    return results && results.facialTransformationMatrixes && results.facialTransformationMatrixes.length > 0
      ? results.facialTransformationMatrixes[0].data
      : null;
  }

  _clearCanvas() {
    this.ctx.clearRect(0, 0, this.els.canvas.width, this.els.canvas.height);
  }

  _buildDots() {
    this.els.guideDots.innerHTML = GUIDE_STEPS.map(() => '<span class="cpapms-dot"></span>').join('');
  }

  _updateRing(progress) {
    const p = Math.max(0, Math.min(1, progress));
    this.els.ringFg.style.strokeDashoffset = String(RING_C * (1 - p));
    this.els.ringPct.textContent = `${Math.round(p * 100)}%`;
  }

  _renderGuideStep() {
    const g = this.state.guided;
    const step = GUIDE_STEPS[g.stepIndex];
    this.els.guideStep.textContent = `Step ${g.stepIndex + 1} of ${GUIDE_STEPS.length}`;
    this.els.guideTitle.textContent = step.title;
    this.els.guideSub.textContent = step.sub;
    this.els.guideSub.dataset.state = '';
    this.els.guideArrow.dataset.dir = step.dir;
    this.els.guideArrow.classList.remove('done');
    this.els.guideRing.classList.remove('complete');
    this._updateRing(0);
    const dots = this.els.guideDots.querySelectorAll('.cpapms-dot');
    dots.forEach((d, i) => {
      d.classList.toggle('active', i === g.stepIndex);
      d.classList.toggle('done', i < g.stepIndex);
    });
  }

  _updateGuideFeedback({ hasFace, posed, steady, centered }) {
    if (!hasFace) {
      this.els.guideSub.textContent = 'Position your face in frame';
      this.els.guideSub.dataset.state = 'wrong';
      return;
    }
    if (centered === false) {
      this.els.guideSub.textContent = 'Move so your whole face is centered in frame';
      this.els.guideSub.dataset.state = 'wrong';
      return;
    }
    const step = GUIDE_STEPS[this.state.guided.stepIndex];
    if (posed && steady) {
      this.els.guideSub.textContent = 'Great — hold it…';
      this.els.guideSub.dataset.state = 'hold';
    } else if (posed && !steady) {
      this.els.guideSub.textContent = 'Hold still…';
      this.els.guideSub.dataset.state = 'hold';
    } else {
      this.els.guideSub.textContent = step.sub;
      this.els.guideSub.dataset.state = '';
    }
  }

  _updateGuidedStatus(q) {
    this.els.qmFrontality.style.width = `${Math.round((q.frontality || 0) * 100)}%`;
    this.els.qmSymmetry.style.width = `${Math.round((q.symmetry || 0) * 100)}%`;
    this.els.qmSteadiness.style.width = `${Math.round((q.steadiness || 0) * 100)}%`;
    const yaw = q.yawDeg == null ? '—' : `${q.yawDeg.toFixed(0)}°`;
    const pitch = q.pitchDeg == null ? '—' : `${q.pitchDeg.toFixed(0)}°`;
    this.els.poseReadout.textContent = `yaw: ${yaw}  pitch: ${pitch}`;
    const g = this.state.guided;
    this.els.sampleCounts.textContent = MEASUREMENT_META.map((m) => `${m.key} ${g.best.count(m.key)}/${TOP_N}`).join('  ·  ');
  }

  _resetGuidedStatus() {
    this.els.qmFrontality.style.width = '0%';
    this.els.qmSymmetry.style.width = '0%';
    this.els.qmSteadiness.style.width = '0%';
    this.els.poseReadout.textContent = 'yaw: —  pitch: —';
  }

  _renderResults(results) {
    this.els.resultsList.innerHTML = MEASUREMENT_META.map((m) => {
      const r = results[m.key] || {};
      const value = r.value;
      const level = r.level || 'Low';
      const conf = r.confidence || 0;
      const warn = m.key === 'ipd' && value != null && (value < 58 || value > 68);
      return `
        <div class="cpapms-result-row${warn ? ' warn' : ''}">
          <span class="cpapms-swatch" style="background:${m.color}"></span>
          <div class="cpapms-result-main">
            <span class="cpapms-result-label">${m.label}</span>
            <span class="cpapms-conf">
              <span class="cpapms-conf-bar"><span class="cpapms-conf-fill" data-level="${level}" style="width:${Math.round(conf * 100)}%"></span></span>
              <span>${level}${r.n != null ? ` · n=${r.n}` : ''}</span>
            </span>
          </div>
          <span class="cpapms-result-value">${value != null ? value.toFixed(1) + ' mm' : '—'}</span>
        </div>`;
    }).join('');
    this._updateRecommendation();
  }

  _updateRecommendation() {
    const g = this.state.guided;
    if (!g.results) return;
    const family = (this.config.families || [])[this.state.selectedFamilyIndex];
    if (!family) {
      this.els.recommendResult.innerHTML = '';
      this.els.cartAction.innerHTML = '';
      return;
    }
    // Clear any leftover "Added to cart" message from a previous family or
    // a previous measurement — this is a fresh recommendation now.
    this.els.cartStatus.classList.add('hidden');
    this.els.cartStatus.textContent = '';

    const measurements = {};
    for (const m of MEASUREMENT_META) measurements[m.key] = g.results[m.key] ? g.results[m.key].value : null;

    // The size-mapping thresholds are all sourced from adult sizing guides. IPD
    // is the one measurement we have real confidence is accurate across ages
    // (validated against a 5-year-old's real-world IPD norm during testing) —
    // when it falls outside the typical adult band, treat that as a signal the
    // OTHER measurements (nose height especially) may not be reliable, since
    // MediaPipe's landmark model likely doesn't generalize as well to child
    // facial proportions as it does to adult ones. This is a caveat, not a
    // hard block — plenty of adults have an IPD outside 58-68mm too.
    const ipd = measurements.ipd;
    const outsideAdultRange = ipd != null && (ipd < 58 || ipd > 68);
    const adultRangeCaveat = outsideAdultRange
      ? `<p class="cpapms-recommend-adult-caveat">Your interpupillary distance (${ipd.toFixed(1)}mm) is outside the typical adult range (58–68mm) this tool is built and tested for — the size suggestion below may not be reliable (this is more likely for children, where our nose measurements haven't been validated). Please confirm with the printable sizing template.</p>`
      : '';

    const guideUrl = this._esc(family.sizingPageUrl || '/pages/mask-sizing');
    const guideLink = `<a href="${guideUrl}">${this._esc(family.name)} sizing &amp; care guide</a>`;

    const rec = recommendSize(family, measurements);
    if (rec.confidence === CONFIDENCE.UNSUPPORTED || rec.size == null) {
      this.els.recommendResult.innerHTML = `
        ${adultRangeCaveat}
        <p class="cpapms-recommend-unsupported">We don't have a reliable webcam-based size estimate for ${this._esc(family.name)} yet — see the ${guideLink} instead.</p>`;
      this.els.cartAction.innerHTML = '';
      return;
    }
    // Always link back to this mask's own sizing & care page — previously
    // only shown for non-HIGH confidence, but v1 families are almost all
    // HIGH, so in practice the link never appeared.
    this.els.recommendResult.innerHTML = `
      ${adultRangeCaveat}
      <p class="cpapms-recommend-size">Recommended size for ${this._esc(family.name)}: <strong>${rec.size}</strong></p>
      <p class="cpapms-recommend-caveat">See the ${guideLink} to confirm.</p>`;
    // Rendered as the single clear primary action for this screen — kept
    // OUTSIDE the grey recommend box and visually distinct from "Measure
    // again" / "Choose a different mask" below, which are corrections, not
    // the goal of this screen (adding to cart is).
    this.els.cartAction.innerHTML = this._cartActionHtml(family, rec);
  }

  // Shopify's "Small"/"Medium"/"Large" variant option values -> our S/M/L
  // size codes. Handles the short form too in case a future family's
  // variants are labelled that way instead.
  _normalizeSizeLabel(label) {
    const s = String(label || '').trim().toLowerCase();
    if (s === 's' || s.startsWith('small')) return 'S';
    if (s === 'm' || s.startsWith('med')) return 'M';
    if (s === 'l' || s.startsWith('large')) return 'L';
    return null;
  }

  // A real product preview — image, vendor, title, price (with a sale badge
  // when applicable) — not just a bare "Add to cart" button, so the customer
  // can see what they're about to buy without leaving this screen. Only
  // offers the actual "Add to cart" action when we can confidently match the
  // recommended size to an in-stock variant; otherwise falls back to "View
  // product" (e.g. fit-pack families like Eson 2 sell as one non-sized
  // SKU — there's no size-specific variant to add) rather than guessing.
  _cartActionHtml(family, rec) {
    const product = family.product;
    if (!product) return '';
    const variants = product.sizeVariants || [];
    const match = variants.find((v) => v.available && this._normalizeSizeLabel(v.label) === rec.size);
    const priced = match || variants[0]; // show A price even when falling back to "View product"
    const cartIcon = '<svg viewBox="0 0 24 24"><path d="M3 4h2l2 12h12l2-8H6"/><circle cx="9" cy="20" r="1.4"/><circle cx="17" cy="20" r="1.4"/></svg>';

    const media = product.image
      ? `<img src="${this._esc(product.image)}" alt="${this._esc(product.title)}" loading="lazy">`
      : '';
    const badge = priced && priced.compareAtPriceHtml ? `<span class="cpapms-product-badge">Sale</span>` : '';
    const priceHtml = priced
      ? `<span class="cpapms-price">${priced.priceHtml}</span>${priced.compareAtPriceHtml ? `<span class="cpapms-price-compare">${priced.compareAtPriceHtml}</span>` : ''}`
      : '';
    const card = `
      <div class="cpapms-product-card">
        <div class="cpapms-product-media">${media}${badge}</div>
        <div class="cpapms-product-info">
          ${product.vendor ? `<div class="cpapms-product-vendor">${this._esc(product.vendor)}</div>` : ''}
          <div class="cpapms-product-title">${this._esc(product.title)}</div>
          ${priceHtml ? `<div class="cpapms-product-price">${priceHtml}</div>` : ''}
        </div>
      </div>`;

    if (match) {
      return `${card}<button type="button" class="cpapms-btn cpapms-btn-primary cpapms-btn-cart cpapms-add-to-cart" data-variant-id="${match.variantId}">${cartIcon}Add to cart — ${this._esc(match.label)}</button>`;
    }
    if (product.url) {
      return `${card}<a class="cpapms-btn cpapms-btn-primary cpapms-btn-cart" href="${this._esc(product.url)}">View product</a>`;
    }
    return '';
  }

  async _addToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const root = (window.Theme && window.Theme.routes && window.Theme.routes.root) || '/';
    btn.disabled = true;
    const origText = btn.textContent;
    btn.textContent = 'Adding…';
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const json = await res.json();
      if (json && json.status) {
        this._setCartStatus(json.description || json.message || 'Could not add to cart.', 'error');
        return;
      }
      // Same pattern as cpap-mask-finder.js — Horizon's cart drawer picks
      // this up and opens/refreshes itself, so no bespoke drawer UI needed here.
      document.dispatchEvent(new CartAddEvent({}, 'cpap-mask-size-scanner', { source: 'cpap-mask-size-scanner' }));
      this._setCartStatus('Added to cart.', 'ok');
    } catch (err) {
      this._setCartStatus('Something went wrong — please try again.', 'error');
    } finally {
      btn.disabled = false;
      btn.textContent = origText;
    }
  }

  _setCartStatus(msg, kind) {
    this.els.cartStatus.textContent = msg;
    this.els.cartStatus.dataset.kind = kind;
    this.els.cartStatus.classList.remove('hidden');
  }

  _resultsToText(results) {
    const lines = ['Face Mask Sizer — measurements'];
    for (const m of MEASUREMENT_META) {
      const r = results[m.key] || {};
      const v = r.value != null ? `${r.value.toFixed(1)} mm` : '—';
      lines.push(`${m.label}: ${v} (${r.level || 'Low'}, n=${r.n || 0})`);
    }
    return lines.join('\n');
  }

  _resetGuidedSession() {
    const g = this.state.guided;
    g.stepIndex = 0;
    g.holdMs = 0;
    g.lastTs = null;
    g.prevKeyPts = null;
    g.locking = false;
    g.finished = false;
    g.results = null;
    g.baseline = null;
    g.lastPose = null;
    g.best.reset();
    this._renderGuideStep();
    this._resetGuidedStatus();
    // Clear a leftover "based on your measurements from [old date]" note from
    // a previous stored-data view — a fresh capture is about to replace it.
    this.els.resultsDate.classList.add('hidden');
  }

  _drawLandmarkDots(landmarks) {
    const w = this.els.canvas.width;
    const h = this.els.canvas.height;
    this.ctx.fillStyle = 'rgba(255,255,255,0.35)';
    for (const lm of landmarks) {
      this.ctx.beginPath();
      this.ctx.arc(lm.x * w, lm.y * h, 1, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }

  _drawMeasurementLine(a, b, color, mm) {
    const ctx = this.ctx;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.fillStyle = color;
    for (const p of [a, b]) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
      ctx.fill();
    }
    if (mm != null) {
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      const label = `${mm.toFixed(0)}`;
      ctx.save();
      ctx.translate(midX, midY);
      ctx.scale(-1, 1);
      ctx.font = '14px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const pad = 3;
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillRect(-tw / 2 - pad, -9, tw + pad * 2, 18);
      ctx.fillStyle = color;
      ctx.fillText(label, 0, 0);
      ctx.restore();
    }
  }

  _drawOverlay(landmarks, result, frontal) {
    this._clearCanvas();
    this._drawLandmarkDots(landmarks);
    for (const m of MEASUREMENT_META) {
      const d = result.measurements[m.key];
      this._drawMeasurementLine(d.a, d.b, m.color, frontal ? d.mm : null);
    }
  }

  _handleGuidedFrame(results, hasFace, now) {
    const g = this.state.guided;
    if (g.finished) {
      this._clearCanvas();
      if (hasFace) this._drawLandmarkDots(results.faceLandmarks[0]);
      return;
    }
    if (!hasFace) {
      this._clearCanvas();
      g.holdMs = 0;
      g.prevKeyPts = null;
      g.lastTs = now;
      if (!g.locking) this._updateRing(0);
      this._updateGuideFeedback({ hasFace: false });
      this._setStatus('No face detected.', 'info');
      return;
    }

    const landmarks = results.faceLandmarks[0];
    const w = this.els.canvas.width;
    const h = this.els.canvas.height;
    const result = computeAll(landmarks, w, h);
    const pose = poseAngles({ matrix: this._matrixOf(results), landmarks, width: w, height: h });
    g.lastPose = pose; // so _lockStep() can snapshot it as the baseline when Center locks

    const keyPts = keyPointsPx(landmarks, w, h);
    const disp = meanDisplacement(g.prevKeyPts, keyPts);
    g.prevKeyPts = keyPts;
    const steadiness = steadinessScore(disp, result.irisPx, { sensitivity: STEADY_SENSITIVITY });
    const symmetry = symmetryScore(landmarks, w, h);
    // Quality/frontality intentionally uses the RAW pose, not baseline-relative —
    // an honestly bad camera angle should lower measurement confidence, not get
    // calibrated away (see relativePose's docs in cpap-mask-size-measurements.js).
    const frontality = frontalityScore(pose.yawDeg, pose.pitchDeg);
    const quality = qualityScore({ frontality, symmetry, steadiness });

    if (result.scale != null) {
      const mmByKey = { scale: result.scale };
      for (const m of MEASUREMENT_META) mmByKey[m.key] = result.measurements[m.key].mm;
      g.best.add(mmByKey, quality);
    }

    this._drawOverlay(landmarks, result, frontality > 0.5);
    this._updateGuidedStatus({ frontality, symmetry, steadiness, yawDeg: pose.yawDeg, pitchDeg: pose.pitchDeg });

    const step = GUIDE_STEPS[g.stepIndex];
    const centered = isCentered(landmarks, w, h);
    // Center is matched on the RAW pose (it's what defines the baseline) plus a
    // framing check; Up/Down/Left/Right are matched relative to that baseline so
    // an off-axis camera's neutral-pose bias (see relativePose) doesn't block them.
    const posed =
      step.dir === 'center'
        ? matchesPose('center', pose.yawDeg, pose.pitchDeg, POSE_THRESHOLDS) && centered
        : matchesPose(step.dir, relativePose(pose, g.baseline).yawDeg, relativePose(pose, g.baseline).pitchDeg, POSE_THRESHOLDS);
    const steady = steadiness >= STEADY_ADVANCE;

    if (g.locking) {
      g.lastTs = now;
      return;
    }

    const dt = g.lastTs == null ? 0 : Math.min(now - g.lastTs, 100);
    g.lastTs = now;
    if (posed && steady) g.holdMs += dt;
    else g.holdMs = 0;

    this._updateRing(g.holdMs / HOLD_MS);
    this._updateGuideFeedback({ hasFace: true, posed, steady, centered: step.dir === 'center' ? centered : true });
    this._setStatus(posed ? 'Hold the pose steady…' : step.title, posed ? 'ok' : 'info');

    if (g.holdMs >= HOLD_MS) this._lockStep();
  }

  _lockStep() {
    const g = this.state.guided;
    if (g.locking) return;
    const finishedStep = GUIDE_STEPS[g.stepIndex];
    if (finishedStep.dir === 'center' && g.lastPose) {
      // Snapshot this camera's neutral pose now — every later step's target is
      // relative to this, not an absolute constant (see relativePose).
      g.baseline = { yawDeg: g.lastPose.yawDeg, pitchDeg: g.lastPose.pitchDeg };
    }
    g.locking = true;
    this._updateRing(1);
    this.els.guideRing.classList.add('complete');
    this.els.guideArrow.classList.add('done');
    this.els.guideSub.textContent = 'Locked ✓';
    this.els.guideSub.dataset.state = 'hold';

    setTimeout(() => {
      g.locking = false;
      g.holdMs = 0;
      g.prevKeyPts = null;
      g.stepIndex += 1;
      if (g.stepIndex >= GUIDE_STEPS.length) this._finishGuided();
      else this._renderGuideStep();
    }, 450);
  }

  _finishGuided() {
    const g = this.state.guided;
    g.finished = true;
    g.results = g.best.result();
    saveMeasurements(g.results);
    // Release the camera as soon as we have what we need — don't leave it
    // running while the customer reads their results. "Measure again" (via
    // _start()) is the only way back into a capture from here.
    this.state.running = false;
    if (this.state.rafId) cancelAnimationFrame(this.state.rafId);
    this.state.rafId = null;
    this._stopStream();
    this._renderResults(g.results);
    this._setStep(STEP.RESULTS);
    this._clearCanvas();
    this._setStatus('Capture complete — review your measurements.', 'ok');
  }

  _checkEnvironment() {
    if (!window.isSecureContext) {
      this._setStatus('Insecure context — camera access needs https://.', 'error');
      return false;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this._setStatus('This browser does not support camera access.', 'error');
      return false;
    }
    return true;
  }

  async _loadModel() {
    if (this.state.faceLandmarker) return this.state.faceLandmarker;
    this._setStatus('Loading face model… (first load downloads ~a few MB)', 'info');
    try {
      const filesetResolver = await FilesetResolver.forVisionTasks(VISION_WASM);
      this.state.faceLandmarker = await FaceLandmarker.createFromOptions(filesetResolver, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
        runningMode: 'VIDEO',
        numFaces: 1,
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
      });
      return this.state.faceLandmarker;
    } catch (err) {
      console.error(err);
      this._setStatus('Model failed to load. Check your network and reload.', 'error');
      throw err;
    }
  }

  async _startCamera() {
    this._stopStream();
    const constraints = { video: { facingMode: this.state.facingMode, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false };
    try {
      this.state.stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      console.error(err);
      if (err && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
        this._setStatus('Camera permission denied. Allow camera access and try again.', 'error');
      } else if (err && (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError')) {
        this._setStatus('No camera found on this device.', 'error');
      } else {
        this._setStatus(`Could not start camera: ${err && err.name ? err.name : err}`, 'error');
      }
      throw err;
    }
    this.els.video.srcObject = this.state.stream;
    await this.els.video.play();
    await this._waitForVideoDimensions();
    this._resizeCanvas();
  }

  _waitForVideoDimensions() {
    return new Promise((resolve) => {
      if (this.els.video.videoWidth > 0) return resolve();
      this.els.video.addEventListener('loadedmetadata', () => resolve(), { once: true });
    });
  }

  _resizeCanvas() {
    this.els.canvas.width = this.els.video.videoWidth;
    this.els.canvas.height = this.els.video.videoHeight;
  }

  _stopStream() {
    if (this.state.stream) {
      for (const track of this.state.stream.getTracks()) track.stop();
      this.state.stream = null;
    }
    this.els.video.srcObject = null;
  }

  _renderLoop() {
    if (!this.state.running) return;
    const video = this.els.video;
    if (video.readyState >= 2 && video.videoWidth > 0) {
      if (this.els.canvas.width !== video.videoWidth) this._resizeCanvas();
      const now = performance.now();
      if (video.currentTime !== this.state.lastVideoTime) {
        this.state.lastVideoTime = video.currentTime;
        let results;
        try {
          results = this.state.faceLandmarker.detectForVideo(video, now);
        } catch (err) {
          console.error(err);
        }
        const hasFace = !!(results && results.faceLandmarks && results.faceLandmarks.length > 0);
        this._handleGuidedFrame(results, hasFace, now);
      }
    }
    this.state.rafId = requestAnimationFrame(() => this._renderLoop());
  }

  async _start() {
    if (this.state.selectedFamilyIndex == null) return;
    if (!this._checkEnvironment()) return;
    this.els.startBtn.disabled = true;
    try {
      await this._loadModel();
      await this._startCamera();
    } catch (err) {
      this.els.startBtn.disabled = false;
      return;
    }
    this.state.running = true;
    this.state.lastVideoTime = -1;
    this.els.stopBtn.disabled = false;
    this.els.switchBtn.disabled = false;
    this._resetGuidedSession();
    this._setStatus('Guided capture — follow the on-screen prompts.', 'ok');
    this._setStep(STEP.CAPTURE);
    this.state.rafId = requestAnimationFrame(() => this._renderLoop());
  }

  _stop() {
    this.state.running = false;
    if (this.state.rafId) cancelAnimationFrame(this.state.rafId);
    this.state.rafId = null;
    this._stopStream();
    this._clearCanvas();
    this.els.startBtn.disabled = false;
    this.els.stopBtn.disabled = true;
    this.els.switchBtn.disabled = true;
    this._setStatus('Stopped.', 'info');
    this._setHint(null);
  }

  async _switchCamera() {
    this.state.facingMode = this.state.facingMode === 'user' ? 'environment' : 'user';
    this._setStatus(`Switching to ${this.state.facingMode === 'user' ? 'front' : 'back'} camera…`, 'info');
    try {
      await this._startCamera();
      this.state.lastVideoTime = -1;
    } catch (err) {
      this.state.facingMode = this.state.facingMode === 'user' ? 'environment' : 'user';
    }
  }
}

if (!customElements.get('cpap-mask-size-scanner')) customElements.define('cpap-mask-size-scanner', CpapMaskSizeScanner);
